import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  NotificationService,
  Error,
  CustomHttpErrorResponse,
  downloadBlob,
  REDIRECTION_BASIC_DELAY,
} from '../../../../common';
import {
  PublicProofOfDeliveryDownloadFile,
  PublicProofOfDeliveryFeedback,
  PublicProofOfDeliveryInitialState,
  PublicProofOfDeliveryLoad,
  PublicProofOfDeliverySave,
} from './public-proof-of-delivery.actions';
import { PublicProofOfDeliveryService } from './public-proof-of-delivery.service';
import { PublicProofOfDeliveryItem } from './models/public-proof-of-delivery.model';
import { HttpResponse } from '@angular/common/http';
import { ROOT_ENV } from '../../../../services';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface PublicProofOfDeliveryStateModel extends PublicProofOfDeliveryItem {
  loaded: boolean;
}

const initialState: PublicProofOfDeliveryStateModel = {
  ...new PublicProofOfDeliveryItem(),
  loaded: false,
};

@State<PublicProofOfDeliveryStateModel>({
  name: 'publicProofOfDelivery',
  defaults: initialState,
})
@Injectable()
export class PublicProofOfDeliveryState {
  @Selector()
  static getState(state: PublicProofOfDeliveryStateModel): PublicProofOfDeliveryStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: PublicProofOfDeliveryStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private publicProofOfDeliveryService: PublicProofOfDeliveryService,
    private notificationService: NotificationService,
  ) {}

  @Action(PublicProofOfDeliveryLoad)
  publicProofOfDeliveryLoad(
    ctx: StateContext<PublicProofOfDeliveryStateModel>,
    { payload }: PublicProofOfDeliveryLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.publicProofOfDeliveryService.getPublicProofOfDeliveryItem(payload).pipe(
      map((podItem: PublicProofOfDeliveryItem) => {
        ctx.patchState({ ...podItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        return of(this.notificationService.error(error.message)).pipe(
          delay(REDIRECTION_BASIC_DELAY),
          tap(() => window.open(ROOT_ENV, '_self')),
        );
      }),
    );
  }

  @Action(PublicProofOfDeliveryDownloadFile)
  downloadExportsFile(
    ctx: StateContext<PublicProofOfDeliveryStateModel>,
    { payload }: PublicProofOfDeliveryDownloadFile,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.publicProofOfDeliveryService.downloadFile(payload).pipe(
      map((response: HttpResponse<Blob>) => {
        const { body } = response;
        downloadBlob(body, payload.name);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        return of(this.notificationService.error(error.message)).pipe(
          delay(REDIRECTION_BASIC_DELAY),
          tap(() => window.open(ROOT_ENV, '_self')),
        );
      }),
    );
  }

  @Action(PublicProofOfDeliverySave)
  saveProofOfDeliveryItem(
    ctx: StateContext<PublicProofOfDeliveryStateModel>,
    { payload }: PublicProofOfDeliverySave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.publicProofOfDeliveryService.savePublicProofOfDeliveryItem(payload).pipe(
      map((podItem: PublicProofOfDeliveryItem) => {
        ctx.patchState({ ...podItem, loaded: true });
        this.notificationService.success('api.pod.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        if (error.status === 500) {
          return of(this.notificationService.error(error.message));
        } else {
          return of(this.notificationService.error(error.message)).pipe(
            delay(REDIRECTION_BASIC_DELAY),
            tap(() => {
              window.open(ROOT_ENV, '_self');
            }),
          );
        }
      }),
    );
  }

  @Action(PublicProofOfDeliveryFeedback)
  sendProofOfDeliveryFeedback(
    ctx: StateContext<PublicProofOfDeliveryStateModel>,
    { payload }: PublicProofOfDeliveryFeedback,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { token, data, dialogRef } = payload;

    return this.publicProofOfDeliveryService.sendPublicProofOfDeliveryFeedback({ token, data }).pipe(
      map(() => {
        this.notificationService.success('api.proofOfDeliveryReportIssue.success.send');
        dialogRef.close();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PublicProofOfDeliveryInitialState)
  setInitialState(ctx: StateContext<PublicProofOfDeliveryStateModel>): void {
    ctx.setState(initialState);
  }
}
